/**
 * Promote the newest async (scheduled/panel) reforecast of a forecast ROW into its live run.
 * This is the shared core behind both the on-load overlay (`useForecastPersistence`) and the
 * push-driven read-through (`commands/reconcile`), so the two cannot drift.
 *
 * Reads the live run via `getRunState()` (the external store) and dispatches via the
 * module-level `dispatchRun`, so callers pass no store refs. The run may live in either
 * slice, owner (`runsById`) or shared-with-me (`sharedRunsById`), and is looked up with
 * `selectRun`; `runReforecasted` writes back to whichever slice holds it.
 */

import { dispatchRun, getRunState } from "../../store/runStore.js";
import { runPointersMerged, runReforecasted } from "./reducer.js";
import { selectRun } from "./selectors.js";
import { mergeReforecastEvents } from "./reforecastEvents.js";
import {
  newestReforecastPatch,
  selectNewestReforecast,
  shouldApplyNewestReforecast,
} from "./reforecastEntries.js";
import { fetchForecastReport } from "./reportPointer.js";
import { rowRunId } from "./hydrate.js";
import { wouldDowngradeOutcomeSpace } from "./outcomeSpace.js";
import { withLiveIdentity } from "./vintageContract.js";

interface HistoryEntry {
  snapshotAt?: string;
  /** Pre-feature entries carry only this. Read when dating, never used to date. */
  generatedAt?: string;
}

/**
 * When the vintage this fire supersedes was PRODUCED, or null when it is unknowable.
 *
 * Only EXACT moments qualify; an unknown one files nothing and shows no mark. `createdAt`
 * is exact for an EMPTY trail only — on a run with history it predates every vintage.
 */
function datePriorVintage(
  priorHistory: HistoryEntry[],
  lastReforecastedAt: string | null | undefined,
  createdAt: string | null | undefined,
): string | null {
  if (lastReforecastedAt) return lastReforecastedAt;
  if (!priorHistory.length) return createdAt || null;
  // This vintage began the instant the previous one ended. A pre-feature entry has no
  // `snapshotAt`, so that instant is unknowable — and this is where we stop.
  return priorHistory[priorHistory.length - 1]?.snapshotAt ?? null;
}

/** Whether the forecast pointer naming `rowKey` is an on-demand run's. */
function isOnDemandPointer(pointers: unknown[] | undefined, rowKey: string): boolean {
  return (Array.isArray(pointers) ? pointers : []).some(
    (p) =>
      !!p &&
      typeof p === "object" &&
      (p as { kind?: unknown }).kind === "forecast" &&
      (p as { row_key?: unknown }).row_key === rowKey &&
      (p as { source?: unknown }).source === "ondemand",
  );
}

interface AnswerLike {
  serverPrediction?: unknown;
  forecastValue?: unknown;
  contract?: { probability?: unknown; forecast_value?: unknown } | null;
}

/**
 * Whether a fetched fire gives the same answer as the live run. When both sides carry a
 * distribution it decides alone: a non-boolean live run holds a placeholder headline of 0
 * where its fetched report holds null. Otherwise the headlines must match. Unknown on
 * either side is not "same".
 */
function sameAnswer(live: AnswerLike | null | undefined, fire: AnswerLike): boolean {
  if (!live) return false;
  // The representation's numbers only: key order may differ between the stored and fetched copies.
  const dist = (r: AnswerLike) => {
    const fv = (r.contract?.forecast_value ?? r.forecastValue) as {
      representation?: Record<string, unknown>;
    } | null;
    const rep = fv?.representation;
    return rep ? JSON.stringify([rep.form, rep.mass, rep.mean, rep.sd]) : null;
  };
  const distA = dist(live);
  const distB = dist(fire);
  if (distA !== null && distB !== null) return distA === distB;
  const headline = (r: AnswerLike) => {
    const p = Number.isFinite(r.serverPrediction) ? r.serverPrediction : r.contract?.probability;
    return Number.isFinite(p) ? (p as number) : null;
  };
  const a = headline(live);
  return a !== null && a === headline(fire);
}

/** A deliberately loose subset of the wire forecast row: only the fields this module reads. */
export interface ForecastRowLike {
  id?: string;
  forecastId?: string;
  createdAt?: string;
  history?: HistoryEntry[];
  reportPointers?: unknown[];
}

/** `fetchReport` is injectable so tests avoid the network. */
export interface PickupDeps {
  apiGet: (url: string, options?: unknown) => Promise<unknown>;
  cache: { get: (k: string) => unknown; set: (k: string, v: unknown) => unknown };
  fetchReport?: typeof fetchForecastReport;
  /**
   * True once the caller's boot has been abandoned (App unmounted).
   *
   * This function awaits a report fetch and THEN dispatches, so without it a task
   * started by one boot can land in the store a remount has already reset. The
   * realtime/push callers pass nothing, since their dispatch is not tied to a boot's
   * lifetime.
   */
  cancelled?: () => boolean;
}

/**
 * Merge a fresh row's report-pointer history onto its live run — unconditionally, and
 * independently of whether `applyNewestReforecast` finds a fire to promote.
 *
 * The independence matters because an ON-DEMAND completion's pointer is the run's own
 * generation pointer, not an async fire, so `applyNewestReforecast` bails on it
 * (`selectNewestReforecast` → not `async`).
 *
 * Every read-through entry point calls this, so the client learns its pointers whichever
 * route the row arrived by. The merge itself lives in the reducer
 * (`RUN_POINTERS_MERGED` → reportPointerMerge), which is the single owner of the field.
 */
export function mergeRowReportPointers(row: ForecastRowLike | null | undefined): void {
  if (!row) return;
  const runId = rowRunId(row);
  if (!runId || !Array.isArray(row.reportPointers)) return;
  dispatchRun(runPointersMerged(runId, row.reportPointers));
}

/**
 * Best-effort: returns `null` (no dispatch) when there is no newer async fire, the report
 * can't be fetched/mapped, or the live run has moved on (a concurrent manual reforecast,
 * cancel, or already-at-or-newer state), so a re-delivered event is a no-op.
 *
 * @returns the promoted fire's `moment` (its ISO `fire_ts`) iff a `runReforecasted`
 *   was dispatched, else `null`.
 */
export async function applyNewestReforecast(
  row: ForecastRowLike,
  deps: PickupDeps,
): Promise<string | null> {
  const { apiGet, cache, fetchReport = fetchForecastReport, cancelled } = deps;
  if (!row) return null;
  if (cancelled?.()) return null;
  const history = Array.isArray(row.history) ? row.history : [];
  // The last history entry's `snapshotAt` is the live/manual reforecast moment
  // (== the run's lastReforecastedAt at push time); its `generatedAt` would be
  // the PRIOR forecast's moment and under-count the live moment. Fall back to
  // createdAt for a never-manually-reforecasted row. Same convention as the `oldSnapshot`
  // built in commands/reforecast.ts.
  const liveMomentIso =
    (history.length ? history[history.length - 1]?.snapshotAt : null) || row.createdAt || null;
  const newest = selectNewestReforecast(liveMomentIso, row.reportPointers ?? []);
  if (newest.kind !== "async") return null;
  const runId = rowRunId(row);
  if (!runId) return null;
  // Cheap pre-fetch guard that only skips a wasted report fetch; the post-fetch check
  // below is authoritative.
  if (!shouldApplyNewestReforecast(selectRun(getRunState(), runId), newest.fireTs)) return null;
  const src = await fetchReport(newest.rowKey, { apiGet, cache });
  // Re-checked AFTER the fetch, not only before it: the fetch is where an unmount
  // has time to happen, and the dispatch below is what would reach the new store.
  if (cancelled?.()) return null;
  // Promote only a usable fetched forecast: it must carry a contract, so we never
  // replace a good live contract with a contract-less/empty source.
  if (!src || !src.contract) return null;
  // Re-check the LIVE run AFTER the async fetch — a user action (manual reforecast,
  // cancel) may have landed during it; dispatching against a stale snapshot would
  // clobber that fresher state.
  const liveRun = selectRun(getRunState(), runId);
  if (!shouldApplyNewestReforecast(liveRun, newest.fireTs)) return null;
  // A LEAN run is not a vintage: its `contract` and `events` are null/empty, so promoting here
  // would merge against a blank timeline. Bail: the run's newer forecast is what makes the
  // detail loader read it (commands/loadForecastDetail.ts hasNewerForecast), and filling it
  // re-invokes this overlay, so it is deferred, not dropped.
  if (liveRun?.detailPending) return null;
  // NEVER let a newer vintage change the question's SHAPE. A binary vintage of a
  // multi-value question is not a fresher answer to the same question, so refuse it and
  // keep the forecast the user already has: a stale distribution is honest, a fresh binary
  // one is not. The reverse is NOT a symmetric guard — a binary question gaining a space
  // is a real (re-authored) change and still promotes.
  if (wouldDowngradeOutcomeSpace(liveRun?.contract, src.contract)) return null;
  // Idempotency: an on-demand fire that says exactly what the live run says is the live run
  // itself, not a new version. Its pointer's whole-second fire_ts (the forecast server's
  // run_key) often lands just after the app's millisecond start stamp, so it reads as newer.
  // Promoting it would file the live version into history and show it twice (SOO-776).
  if (isOnDemandPointer(row.reportPointers, newest.rowKey) && sameAnswer(liveRun, src)) return null;
  // Accumulate the past / refresh the future, same as the manual reforecast path
  // (commands/reforecast.ts). Real wall-clock is the boundary here: the scheduled/realtime
  // pickup path has no mock-today override (that's a manual-reforecast debug lever).
  const fresh = newestReforecastPatch(src, newest.fireTs);
  // The fire's contract REPLACES the live one, so a fire that lacks the question's identity
  // keeps the live question, criteria and dates rather than wiping them. The fire's own
  // answer fields are untouched — see withLiveIdentity.
  const patch = { ...fresh, contract: withLiveIdentity(fresh.contract, liveRun?.contract) };
  // A fire says nothing about the timeline when its report carried no trigger block at all
  // (`src.events` null — the absence `reportPayloadToSource` preserves), or when its
  // trigger-gen FAILED, which makes whatever it emitted not a statement about the question.
  //
  // Load-bearing because the merge refreshes the FUTURE half from the fresh run alone.
  // Reading "no trigger block" as "zero triggers" therefore refreshes the future from an
  // empty list and DELETES every pending trigger the user had.
  // `newestReforecastPatch` coerces the null to `[]` (the same `[] is truthy`
  // confusion as `asyncEntrySnapshot`), so the check is against `src`, not `patch`.
  //
  // A trigger array that is genuinely EMPTY still refreshes when the run actually looked
  // and found nothing. An explicit `none` is only the transport shape for no attempt, just
  // as `failed` carries no trustworthy timeline claim. Missing status remains compatible
  // with legacy rows whose events array was authoritative before this field existed.
  const fireCarriesTimeline =
    Array.isArray(src.events) &&
    src.postprocessStatus !== "failed" &&
    src.postprocessStatus !== "none";
  // Slide anchors and every other cross-vintage handle key on `occurrenceKey`, which the
  // merge stamps and which does not change when a run renumbers its ids, so no id opt-out
  // is needed.
  const mergedEvents = fireCarriesTimeline
    ? mergeReforecastEvents({
        oldEvents: Array.isArray(liveRun?.events) ? liveRun.events : [],
        newEvents: src.events,
        nowDate: new Date(),
      })
    : // The rest of the fire (probability, contract, report) still promotes; only the
      // timeline is left alone.
      Array.isArray(liveRun?.events)
      ? liveRun.events
      : [];
  // The snapshot of the state this fire SUPERSEDES has the same shape the manual path
  // records (commands/reforecast.ts `oldSnapshot`), so both kinds of vintage read back
  // identically.
  // Captured from `liveRun` BEFORE the merge: `events` is the pre-fire accumulated
  // timeline, not the fire's own trigger output.
  const priorHistory: HistoryEntry[] = Array.isArray(liveRun?.history)
    ? (liveRun.history as HistoryEntry[])
    : [];
  const generatedAt = datePriorVintage(priorHistory, liveRun?.lastReforecastedAt, row.createdAt);
  // No knowable moment → record nothing. An undated entry would be unreachable itself and
  // would leave nothing after it dateable.
  const nextHistory =
    generatedAt == null
      ? undefined
      : [
          ...priorHistory,
          {
            generatedAt,
            snapshotAt: newest.fireTs,
            contract: liveRun?.contract || null,
            events: Array.isArray(liveRun?.events) ? liveRun.events : [],
            report: liveRun?.report || null,
            serverContext: liveRun?.serverContext || null,
            serverExplanation: liveRun?.serverExplanation || null,
            // The headline, which can differ from contract.probability: a rollback restores it.
            serverPrediction: liveRun?.serverPrediction ?? null,
          },
        ];
  // Undateable → no history entry, but the replaced vintage is still what a post-resolution
  // rollback must restore, so it is kept in memory instead (never as a scrub stop).
  const superseded = nextHistory
    ? undefined
    : [
        ...(Array.isArray(liveRun?.superseded) ? liveRun.superseded : []),
        {
          generatedAt: null,
          snapshotAt: newest.fireTs,
          contract: liveRun?.contract || null,
          events: Array.isArray(liveRun?.events) ? liveRun.events : [],
          report: liveRun?.report || null,
          serverContext: liveRun?.serverContext || null,
          serverExplanation: liveRun?.serverExplanation || null,
          serverPrediction: liveRun?.serverPrediction ?? null,
        },
      ];
  dispatchRun(
    runReforecasted(runId, {
      ...patch,
      events: mergedEvents,
      // Omitted (not null) when undateable: `runReforecasted` only touches history when
      // the field is supplied, so the existing trail is left exactly as it stands.
      ...(nextHistory ? { history: nextHistory } : {}),
      ...(superseded ? { superseded } : {}),
      // The modal's LIVE overlay resolves the LATEST report-pointer, so the pointer list must
      // sync with the promote or the two disagree until the next hydrate. row.reportPointers
      // is the authoritative current list (includes the just-arrived fire); manual
      // reforecasts skip this path.
      reportPointers: Array.isArray(row.reportPointers) ? row.reportPointers : undefined,
    }),
  );
  return newest.fireTs;
}
